import React, { useState } from 'react';
import { Button } from '../../ui/common/Button';
import {
  SHORTCUT_DEFINITIONS,
  type ShortcutId,
} from '../../hooks/keyboardShortcutLogic';
import {
  bindingFromKeyboardEvent,
  formatShortcutBinding,
  useKeyboardShortcutSettings,
} from '../../hooks/keyboardShortcutSettings';
import type { ShortcutBinding } from '../../hooks/keyboardShortcutLogic';
import { hintStyle, labelStyle, sectionStyle } from './settingsStyles';

export interface SettingsKeyboardShortcutsSectionProps {
  userId?: string;
}

function sameBinding(left: ShortcutBinding, right: ShortcutBinding): boolean {
  return left.key.toLowerCase() === right.key.toLowerCase() && left.modifier === right.modifier;
}

export const SettingsKeyboardShortcutsSection: React.FC<SettingsKeyboardShortcutsSectionProps> = ({
  userId,
}) => {
  const { bindings, updateBinding, resetBindings } = useKeyboardShortcutSettings(userId);
  const [capturing, setCapturing] = useState<ShortcutId | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const captureBinding = (id: ShortcutId, event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (capturing !== id) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setCapturing(null);
      setMessage('Captura cancelada.');
      return;
    }
    const binding = bindingFromKeyboardEvent(event.nativeEvent);
    if (!binding) return;
    event.preventDefault();
    event.stopPropagation();
    const conflict = SHORTCUT_DEFINITIONS.some(
      ({ id: otherId }) => otherId !== id && sameBinding(bindings[otherId], binding),
    );
    if (conflict) {
      setMessage('Esa combinación ya está asignada a otro atajo.');
      return;
    }
    const persisted = updateBinding(id, binding);
    setCapturing(null);
    setMessage(
      persisted
        ? 'Atajo guardado en este dispositivo.'
        : 'Atajo activo durante esta sesión; no se pudo guardar en este dispositivo.',
    );
  };

  const reset = () => {
    const persisted = resetBindings();
    setCapturing(null);
    setMessage(
      persisted
        ? 'Atajos restaurados.'
        : 'Atajos restaurados durante esta sesión; no se pudo guardar en este dispositivo.',
    );
  };

  return (
    <section style={{ ...sectionStyle, borderBottom: 'none' }} data-testid="settings-shortcuts-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={labelStyle}>Atajos de teclado</div>
        <Button size="sm" variant="ghost" data-testid="shortcut-reset" onClick={reset}>
          Restaurar predeterminados
        </Button>
      </div>
      <p style={{ ...hintStyle, margin: 0 }}>
        Personaliza los atajos globales. Selecciona un atajo y pulsa las teclas nuevas. Se guardan solo en este dispositivo.
      </p>
      {message ? (
        <div data-testid="shortcut-status" role="status" style={{ ...hintStyle, color: 'var(--z-accent-copy)' }}>
          {message}
        </div>
      ) : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {SHORTCUT_DEFINITIONS.map((definition) => {
          const active = capturing === definition.id;
          return (
            <div
              key={definition.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '10px 12px',
                border: '1px solid var(--z-border)',
                borderRadius: 10,
                background: active ? 'var(--z-accent-soft)' : 'var(--z-surface)',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ color: 'var(--z-text)', fontSize: 13, fontWeight: 600 }}>{definition.label}</div>
                <div style={{ ...hintStyle, marginTop: 2 }}>{definition.description}</div>
              </div>
              <Button
                size="sm"
                variant={active ? 'primary' : 'ghost'}
                data-testid={`shortcut-binding-${definition.id}`}
                aria-label={`${definition.label}: ${active ? 'pulsa una combinación' : formatShortcutBinding(bindings[definition.id])}`}
                onClick={() => {
                  setCapturing(definition.id);
                  setMessage(null);
                }}
                onKeyDown={(event) => captureBinding(definition.id, event)}
              >
                {active ? 'Pulsa las teclas…' : formatShortcutBinding(bindings[definition.id])}
              </Button>
            </div>
          );
        })}
      </div>
    </section>
  );
};
